import React from "react";
import { useAuth } from "../hooks/useAuth";
import { Utils } from "../utils/utils";
import { Navigate, useNavigate } from "react-router-dom";
import { 
  Layout, 
  Typography, 
  Button, 
  Card,
  Row, 
  Col,
  Avatar 
} from "antd";
import { 
  HomeOutlined, 
  StarOutlined, 
  PictureOutlined,
  CameraOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CloudDownloadOutlined
} from "@ant-design/icons";
import styled from "styled-components";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 12px 8px;
  }

  @media (max-width: 576px) {
    padding: 10px 6px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  color: #4169e1;
`;

const NavMenu = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;

  &:hover {
    color: #4169e1;
  }

  @media (max-width: 576px) {
    .text {
      display: none;
    }
  }
`;

const MainContent = styled(Content)`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: calc(100vh - 64px - 70px);
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 12px;
  }

  @media (max-width: 576px) {
    padding: 12px 8px;
    width: 100vw;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(65, 105, 225, 0.5);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(65, 105, 225, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(65, 105, 225, 0);
    }
  }
`;

const CircleIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background-color: rgba(65, 105, 225, 0.1);
  border-radius: 50%;
  margin-bottom: 16px;
  font-size: 32px;
  color: #4169e1;
  border: 2px solid rgba(65, 105, 225, 0.2);
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;

  /* Center buttons on all screen sizes */
  justify-content: center;
`;

const HomePage: React.FunctionComponent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function for smooth scrolling to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  if (user) {
    if (Utils.userIsSuperAdmin(user)) {
      return <Navigate to="/dashboard/administration" />;
    }
    return <Navigate to="/dashboard" />;
  }

  return (
    <Layout style={{ backgroundColor: '#f0f5ff' }}>
      <HeaderWrapper>
        <Logo>
          <span style={{ color: '#4169e1', marginRight: '4px' }}>
            <CameraOutlined />
          </span>
          RecogniX
        </Logo>
        <NavMenu>
          <NavItem onClick={() => scrollToSection('hero')}>
            <HomeOutlined />
            <span className="text">Home</span>
          </NavItem>
          <NavItem onClick={() => scrollToSection('features')}>
            <StarOutlined />
            <span className="text">Features</span>
          </NavItem>
          <NavItem onClick={() => scrollToSection('how-it-works')}>
            <InfoCircleOutlined />
            <span className="text">How It Works</span>
          </NavItem>
          <NavItem onClick={() => navigate('/event')}>
            <PictureOutlined />
            <span className="text">Find Your Photos</span>
          </NavItem>
          <NavItem onClick={() => navigate('/dashboard')}>
            <UserOutlined />
            <span className="text">Sign-in</span>
          </NavItem>
        </NavMenu>
      </HeaderWrapper>

      <MainContent>
          <>
            {/* Hero Section */}
            <section id="hero" style={{
              minHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: '60px 20px',
              background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
              borderRadius: '20px',
              marginBottom: '60px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{
                width: '100%',
                maxWidth: '800px',
                margin: '0 auto',
                padding: '0 8px',
                boxSizing: 'border-box'
              }}>
                <CircleIconWrapper style={{ margin: '0 auto 32px auto', width: '100px', height: '100px', fontSize: '44px' }}>
                  <CameraOutlined />
                </CircleIconWrapper>
                <Title style={{
                  fontSize: 'calc(1.8rem + 1.5vw)',
                  marginBottom: '24px',
                  background: 'linear-gradient(to right, #4169e1, #1890ff)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  wordWrap: 'break-word',
                  overflow: 'hidden',
                  lineHeight: '1.2'
                }}>
                  Find Your Photos Instantly with Facial Recognition
                </Title>
                <Title level={3} style={{
                  fontWeight: 'normal',
                  marginBottom: '40px',
                  color: '#666',
                  fontSize: 'calc(1rem + 0.5vw)'
                }}>
                  No more scrolling through endless event galleries. Just upload a selfie and we'll find all your photos in seconds.
                </Title>
                <ButtonContainer>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => scrollToSection('how-it-works')}
                    style={{
                      height: 'auto',
                      padding: '12px 32px',
                      fontSize: '18px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                      border: 'none',
                      boxShadow: '0 8px 16px rgba(65, 105, 225, 0.3)',
                      marginBottom: '12px'
                    }}
                  >
                    <Text style={{ color: 'white' }}>How It Works</Text>
                  </Button>
                  <Button
                    size="large"
                    style={{
                      height: 'auto',
                      padding: '12px 32px',
                      fontSize: '18px',
                      borderRadius: '8px',
                      border: '2px solid #4169e1',
                      color: '#4169e1',
                      marginBottom: '12px'
                    }}
                    onClick={() => navigate('/sign-up')}
                  >
                    <Text style={{ color: '#4169e1' }}>Event Organizers</Text>
                  </Button>
                </ButtonContainer>
              </div>

              <div style={{
                marginTop: '60px',
                width: '100%',
                maxWidth: '100%',
                padding: '0 8px',
                boxSizing: 'border-box'
              }}>
                <img
                  src="/img/svg/product-launch.svg"
                  alt="RecogniX Platform"
                  style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                />
              </div>
            </section>
            
            {/* Features Section */}
            <section id="features" style={{
              padding: '60px 16px',
              marginBottom: '40px',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Title level={2} style={{
                textAlign: 'center',
                marginBottom: '40px',
                color: '#4169e1',
                fontSize: 'calc(1.5rem + 0.5vw)'
              }}>
                Why Choose RecogniX?
              </Title>

              <Row gutter={[16, 32]} style={{
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
                boxSizing: 'border-box'
              }}>
                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <CircleIconWrapper>
                        <CameraOutlined />
                      </CircleIconWrapper>
                      <Title level={4} style={{
                        marginTop: '16px',
                        color: '#4169e1',
                        fontSize: 'calc(1.1rem + 0.2vw)'
                      }}>
                        Instant Photo Finding
                      </Title>
                      <Paragraph style={{ fontSize: '16px' }}>
                        Find all your photos from an event in seconds with our advanced facial recognition technology. No more endless scrolling.
                      </Paragraph>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <CircleIconWrapper>
                        <CloudDownloadOutlined />
                      </CircleIconWrapper>
                      <Title level={4} style={{
                        marginTop: '16px',
                        color: '#4169e1',
                        fontSize: 'calc(1.1rem + 0.2vw)'
                      }}>
                        Easy Download
                      </Title>
                      <Paragraph style={{ fontSize: '16px' }}>
                        Download all your photos with a single click. High-quality images ready to share on social media or print.
                      </Paragraph>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <CircleIconWrapper>
                        <InfoCircleOutlined />
                      </CircleIconWrapper>
                      <Title level={4} style={{
                        marginTop: '16px',
                        color: '#4169e1',
                        fontSize: 'calc(1.1rem + 0.2vw)'
                      }}>
                        Privacy Focused
                      </Title>
                      <Paragraph style={{ fontSize: '16px' }}>
                        Your selfie is only used to match photos securely. We never store your biometric data or use it for other purposes.
                      </Paragraph>
                    </div>
                  </Card>
                </Col>
              </Row>
            </section>
            
            {/* How It Works Section */}
            <section id="how-it-works" style={{
              padding: '60px 16px',
              marginBottom: '40px',
              background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
              borderRadius: '20px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Title level={2} style={{
                textAlign: 'center',
                marginBottom: '40px',
                color: '#4169e1',
                fontSize: 'calc(1.5rem + 0.5vw)'
              }}>
                How It Works
              </Title>

              <Row gutter={[16, 32]} style={{
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
                boxSizing: 'border-box'
              }}>
                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      textAlign: 'center',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{ position: 'relative', marginBottom: '24px' }}>
                      <div style={{
                        position: 'absolute',
                        top: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 16px rgba(65, 105, 225, 0.3)'
                      }}>
                        1
                      </div>
                    </div>
                    <Title level={4} style={{
                      color: '#4169e1',
                      fontSize: 'calc(1.1rem + 0.2vw)'
                    }}>Upload a Selfie</Title>
                    <Paragraph style={{ fontSize: '16px' }}>
                      Take a quick selfie or upload an existing photo of yourself that clearly shows your face.
                    </Paragraph>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      textAlign: 'center',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{ position: 'relative', marginBottom: '24px' }}>
                      <div style={{
                        position: 'absolute',
                        top: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 16px rgba(65, 105, 225, 0.3)'
                      }}>
                        2
                      </div>
                    </div>
                    <Title level={4} style={{
                      color: '#4169e1',
                      fontSize: 'calc(1.1rem + 0.2vw)'
                    }}>AI Matching</Title>
                    <Paragraph style={{ fontSize: '16px' }}>
                      Our AI searches thousands of event photos in seconds to find all the images that include you.
                    </Paragraph>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      textAlign: 'center',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <div style={{ position: 'relative', marginBottom: '24px' }}>
                      <div style={{
                        position: 'absolute',
                        top: '-40px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: '0 8px 16px rgba(65, 105, 225, 0.3)'
                      }}>
                        3
                      </div>
                    </div>
                    <Title level={4} style={{
                      color: '#4169e1',
                      fontSize: 'calc(1.1rem + 0.2vw)'
                    }}>Download & Share</Title>
                    <Paragraph style={{ fontSize: '16px' }}>
                      Download your photos instantly, individually or all at once, and share your memories.
                    </Paragraph>
                  </Card>
                </Col>
              </Row>

              <div style={{
                width: '100%',
                maxWidth: '800px',
                margin: '40px auto 0',
                textAlign: 'center',
                padding: '32px 16px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
                boxSizing: 'border-box'
              }}>
                <Title level={3} style={{
                  color: '#4169e1',
                  marginBottom: '24px',
                  fontSize: 'calc(1.2rem + 0.3vw)'
                }}>Ready to Find Your Photos?</Title>
                <Paragraph style={{
                  fontSize: 'calc(0.9rem + 0.2vw)',
                  marginBottom: '32px'
                }}>
                  Try RecogniX at your next event or contact your event organizer to request RecogniX for your upcoming gatherings.
                </Paragraph>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => navigate('/sign-up')}
                  style={{
                    height: 'auto',
                    padding: '12px 32px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                    border: 'none',
                    boxShadow: '0 8px 16px rgba(65, 105, 225, 0.3)'
                  }}
                >
                  Get Started
                </Button>
              </div>
            </section>
            
            {/* Testimonials Section */}
            <section id="testimonials" style={{
              padding: '60px 16px',
              marginBottom: '40px',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <Title level={2} style={{
                textAlign: 'center',
                marginBottom: '40px',
                color: '#4169e1',
                fontSize: 'calc(1.5rem + 0.5vw)'
              }}>
                What Our Users Say
              </Title>

              <Row gutter={[16, 32]} style={{
                width: '100%',
                maxWidth: '1200px',
                margin: '0 auto',
                boxSizing: 'border-box'
              }}>
                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <Paragraph style={{
                      fontSize: '16px',
                      fontStyle: 'italic',
                      marginBottom: '24px'
                    }}>
                      "RecogniX saved me so much time! I found all my wedding photos in seconds instead of searching through hundreds of images."
                    </Paragraph>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        size={40}
                        icon={<UserOutlined />}
                        style={{
                          background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                          marginRight: '12px'
                        }}
                      />
                      <div>
                        <Text strong>Sarah Johnson</Text>
                        <br />
                        <Text type="secondary">Wedding Guest</Text>
                      </div>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <Paragraph style={{
                      fontSize: '16px',
                      fontStyle: 'italic',
                      marginBottom: '24px'
                    }}>
                      "As an event planner, RecogniX has been a game-changer for our clients. They love being able to find their photos so easily!"
                    </Paragraph>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        size={40}
                        icon={<UserOutlined />}
                        style={{
                          background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                          marginRight: '12px'
                        }}
                      />
                      <div>
                        <Text strong>David Miller</Text>
                        <br />
                        <Text type="secondary">Event Planner</Text>
                      </div>
                    </div>
                  </Card>
                </Col>

                <Col xs={24} md={8}>
                  <Card
                    bordered={false}
                    style={{
                      height: '100%',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)'
                    }}
                    styles={{ body: {padding: '24px 16px'} }}
                  >
                    <Paragraph style={{
                      fontSize: '16px',
                      fontStyle: 'italic',
                      marginBottom: '24px'
                    }}>
                      "We used RecogniX for our corporate conference and received incredible feedback. The technology is impressive and easy to use."
                    </Paragraph>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        size={40}
                        icon={<UserOutlined />}
                        style={{
                          background: 'linear-gradient(135deg, #4169e1 0%, #1890ff 100%)',
                          marginRight: '12px'
                        }}
                      />
                      <div>
                        <Text strong>Michelle Chang</Text>
                        <br />
                        <Text type="secondary">Marketing Director</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
            </section>
          </>
      </MainContent>
    </Layout>
  );
};

export default HomePage;